import { ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { Popover, PopoverPortalContent, PopoverTrigger } from "@/components/_atoms/popover";
import { Cross2Icon } from "@radix-ui/react-icons";

import { useDatatableContext } from "../use-datatable-context.tsx";

import {
  FilterByIdListMode,
  FilterByIdListPayload,
  FilterByIdListPayloadSchema,
} from "./functions/filter-by-id-list.ts";
import { FilterByIdListConfigurationView } from "./filter-by-id-list-configuration-view.tsx";

export interface FilterByIdListItem {
  id: string;
  name: string;
}

export interface FilterByIdListAppliedFilterViewProps<TItem extends FilterByIdListItem> {
  columnId: string;
  itemList: TItem[];
  renderItem: (item: TItem | null, view: "icon" | "text" | "icon-text") => ReactElement;
  renderDisplayView: () => ReactElement;
  hideNullOption?: boolean;
}

export const FilterByIdListAppliedFilterView = <TItem extends FilterByIdListItem>({
  itemList,
  renderItem,
  renderDisplayView,
  columnId,
  hideNullOption,
}: FilterByIdListAppliedFilterViewProps<TItem>) => {
  const { t } = useTranslation("common");
  const { table, state } = useDatatableContext();
  const { columnFilters } = state;
  const [showSelectItemPopover, setShowSelectItemPopover] = useState(false);

  const column = table.getColumn(columnId);

  const filter = useMemo(() => {
    if (!column) {
      return null;
    }
    if (!column.getCanFilter()) {
      return null;
    }
    const filterValue = column.getFilterValue();
    if (!filterValue) {
      return null;
    }
    return filterValue;
    // We also listen to the columnFilters to update the filter value when the user changes the filter mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column, columnFilters]);

  const filterByIdListFilter = useMemo(() => {
    if (!filter) {
      return null;
    }

    const safePayload = FilterByIdListPayloadSchema.safeParse(filter);
    if (!safePayload.success) {
      return null;
    }

    return safePayload.data;
  }, [filter]);

  const filterModes: FilterByIdListMode[] = useMemo(() => {
    if (!filterByIdListFilter) {
      return [];
    }
    return ["included", "not_included"];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const itemListForActiveFilter = useMemo(() => {
    if (!filterByIdListFilter) {
      return [];
    }

    return filterByIdListFilter.idList.flatMap((id) => {
      const item = itemList.find((item) => item.id === id);

      if (!item) {
        return [];
      }
      return item;
    });
  }, [filterByIdListFilter, itemList]);

  const setFilter = (payload: FilterByIdListPayload) => {
    column?.setFilterValue(payload);
  };

  const clearFilter = () => {
    if (!column) {
      return;
    }
    table.setColumnFilters(columnFilters.slice().filter((filterValue) => filterValue.id !== column.id));
  };

  const setFilterMode = (filterMode: FilterByIdListMode) => {
    if (!filterByIdListFilter) {
      return;
    }
    setFilter({
      ...filterByIdListFilter,
      mode: filterMode,
    });
  };

  if (!column) {
    return <></>;
  }

  if (!filter) {
    return <></>;
  }

  if (!filterByIdListFilter) {
    return <></>;
  }

  return (
    <div
      className="flex flex-row flex-nowrap items-center gap-0 whitespace-nowrap rounded-md border p-0 text-xs font-normal"
      data-testid="filter-by-text-list-applied-filter-view"
    >
      <div className="flex flex-row items-center gap-1 px-2 text-xs">{renderDisplayView()}</div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild={true}>
          <Button
            variant="ghost"
            size="xs"
            className="h-6 shrink-0 justify-start overflow-x-hidden px-1.5 text-xs font-normal ring-offset-background     focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
          >
            {filterByIdListFilter.mode === "included" && filterByIdListFilter.idList.length === 1 && <>{t("view.is")}</>}
            {filterByIdListFilter.mode === "included" && filterByIdListFilter.idList.length !== 1 && <>{t("view.isPartOf")}</>}
            {filterByIdListFilter.mode === "not_included" && filterByIdListFilter.idList.length === 1 && <>{t("view.isNot")}</>}
            {filterByIdListFilter.mode === "not_included" &&
              filterByIdListFilter.idList.length !== 1 && <>{t("view.isNotPartOf")}</>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {filterModes.map((mode) => {
            return (
              <DropdownMenuItem
                key={mode}
                onClick={() => {
                  setFilterMode(mode);
                }}
              >
                {mode === "included" && filterByIdListFilter.idList.length === 1 && <>{t("view.is")}</>}
                {mode === "included" && filterByIdListFilter.idList.length !== 1 && <>{t("view.isPartOf")}</>}
                {mode === "not_included" && filterByIdListFilter.idList.length === 1 && <>{t("view.isNot")}</>}
                {mode === "not_included" && filterByIdListFilter.idList.length !== 1 && <>{t("view.isNotPartOf")}</>}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Popover open={showSelectItemPopover} onOpenChange={setShowSelectItemPopover}>
        <PopoverTrigger asChild={true}>
          <Button
            variant="ghost"
            size="xs"
            className="h-6 shrink-0 justify-start overflow-x-hidden px-1.5 text-xs font-normal ring-offset-background     focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
          >
            {filterByIdListFilter.idList.length > 1 && (
              <div className="flex flex-row items-center gap-1">
                {itemListForActiveFilter.map((item) => {
                  return renderItem(item, "icon");
                })}
                {t("view.filterValueCount", { count: filterByIdListFilter.idList.length })}
              </div>
            )}
            {filterByIdListFilter.idList.length === 1 && (
              <>
                {itemListForActiveFilter.length === 0 && renderItem(null, "icon-text")}
                {itemListForActiveFilter.length > 0 && (
                  <>
                    {itemListForActiveFilter.map((item) => {
                      return renderItem(item, "icon-text");
                    })}
                  </>
                )}
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverPortalContent className="z-9999 p-0" side={"bottom"} align={"start"}>
          <FilterByIdListConfigurationView
            columnId={columnId}
            itemList={itemList}
            renderItem={renderItem}
            onFinished={() => {
              setShowSelectItemPopover(false);
            }}
            hideNullOption={hideNullOption}
          />
        </PopoverPortalContent>
      </Popover>
      <Button
        variant="ghost"
        size="xs"
        className="h-6 w-full justify-start overflow-x-hidden px-1.5 text-xs font-normal     focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
        onClick={() => {
          clearFilter();
        }}
        aria-label={t("view.clearFilter")}
      >
        <Cross2Icon className="h-3.5 w-3.5 text-foreground/60" />
      </Button>
    </div>
  );
};
